import type { DNRDatabase } from '@data/db/database';
import type { Document as StoredDocument } from '@data/db/types';
import { currentDeviceId } from '@data/sync/deviceIdentity';
import { nextVersion, recordBusinessMutations } from '@data/sync/recordBusinessMutation';
import { issueDocument, type IssueParams } from './documentService';
import { toPartySnapshot } from './partySnapshot';
import { listParties } from './partyReader';
import { listProducts } from './productReader';
import { ulid } from '../../platform/ids';

export class SalesOrderServiceError extends Error {
  constructor(
    readonly code:
      | 'NO_BUSINESS'
      | 'NOT_FOUND'
      | 'NOT_EDITABLE'
      | 'PARTY_NOT_FOUND'
      | 'NO_LINES'
      | 'NO_ITEM'
      | 'WRONG_BUSINESS'
      | 'SOURCE_NOT_ISSUED'
      | 'ALREADY_CONVERTED'
      | 'SOURCE_LINE_INVALID'
      | 'INVALID_INPUT'
      | 'HAS_CHILDREN',
    message: string,
  ) {
    super(message);
    this.name = 'SalesOrderServiceError';
  }
}

export interface SalesOrderLineInput {
  readonly productId: string;
  readonly quantityMilli: number;
  readonly ratePaise: number;
  readonly description?: string;
  readonly sourceLineId?: string;
  /** Unit of measure for this line (e.g., NOS, SQFT, KG). Defaults to product's unit. */
  readonly unit?: string;
}

export interface SalesOrderInput {
  readonly businessId: string;
  readonly customerId: string;
  readonly docDate: string;
  readonly lines: readonly SalesOrderLineInput[];
  readonly quotationId?: string;
  readonly roundOff?: boolean;
  readonly notes?: string;
  readonly terms?: string;
  readonly placeOfSupplyOverride?: string;
  readonly now?: string;
  readonly deviceId?: string;
}

export async function salesOrderFormOptions(db: DNRDatabase, businessId: string) {
  const [customers, products] = await Promise.all([
    listParties(db as never, businessId, { kind: 'CUSTOMER' }),
    listProducts(db as never, businessId, { activeOnly: true }),
  ]);
  return { customers, products };
}

async function buildSalesOrder(
  db: DNRDatabase,
  input: SalesOrderInput,
  identity?: StoredDocument,
) {
  if (!input.lines.length) throw new SalesOrderServiceError('NO_LINES', 'A sales order needs at least one line.');
  const parsedDate = new Date(`${input.docDate}T00:00:00Z`);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(input.docDate) || Number.isNaN(parsedDate.getTime()) || parsedDate.toISOString().slice(0, 10) !== input.docDate) {
    throw new SalesOrderServiceError('INVALID_INPUT', 'Enter a valid sales order date.');
  }
  for (const line of input.lines) {
    if (!Number.isSafeInteger(line.quantityMilli) || line.quantityMilli <= 0 || !Number.isSafeInteger(line.ratePaise) || line.ratePaise < 0) {
      throw new SalesOrderServiceError('INVALID_INPUT', 'Each order line needs a positive quantity and a non-negative rate.');
    }
  }

  const business = await db.businesses.get(input.businessId);
  if (!business) throw new SalesOrderServiceError('NO_BUSINESS', 'Select a business before creating a sales order.');
  const customer = await db.parties.get(input.customerId);
  if (!customer || customer.kind !== 'CUSTOMER') throw new SalesOrderServiceError('PARTY_NOT_FOUND', 'Choose a valid customer.');
  if (customer.businessId !== input.businessId) throw new SalesOrderServiceError('WRONG_BUSINESS', 'That customer belongs to another business.');

  const products = await Promise.all(input.lines.map(async (line) => {
    const product = await db.products.get(line.productId);
    if (!product || product.businessId !== input.businessId) throw new SalesOrderServiceError('NO_ITEM', 'A selected product is unavailable.');
    const tax = product.taxRateId ? await db.taxRates.get(product.taxRateId) : undefined;
    if (tax && tax.businessId !== input.businessId) throw new SalesOrderServiceError('NO_ITEM', 'A selected product tax rate belongs to another business.');
    const sourceLine = line.sourceLineId ? await db.documentLines.get(line.sourceLineId) : undefined;
    return { line, product, tax, sourceLine };
  }));

  const now = input.now ?? new Date().toISOString();
  const deviceId = input.deviceId ?? await currentDeviceId(db);
  const existing = identity ? await db.documents.get(identity.id) : undefined;
  if (identity && (!existing || existing.docType !== 'SALES_ORDER' || existing.status !== 'DRAFT')) {
    throw new SalesOrderServiceError('NOT_EDITABLE', 'Only sales order drafts can be edited.');
  }
  if (existing && existing.businessId !== input.businessId) throw new SalesOrderServiceError('WRONG_BUSINESS', 'The sales order belongs to another business.');

  const quotationId = existing?.quotationId ?? input.quotationId;
  if (existing?.quotationId && input.quotationId && existing.quotationId !== input.quotationId) {
    throw new SalesOrderServiceError('NOT_EDITABLE', 'A sales order cannot be reassigned to another quotation.');
  }
  if (!quotationId && input.lines.some((line) => line.sourceLineId)) {
    throw new SalesOrderServiceError('SOURCE_LINE_INVALID', 'A source line requires a quotation conversion.');
  }
  if (quotationId && !input.lines.some((line) => line.sourceLineId)) {
    throw new SalesOrderServiceError('SOURCE_LINE_INVALID', 'Keep at least one line linked to the source quotation.');
  }

  const source = quotationId ? await db.documents.get(quotationId) : undefined;
  if (quotationId && (!source || source.docType !== 'QUOTATION' || source.businessId !== input.businessId)) {
    throw new SalesOrderServiceError('SOURCE_LINE_INVALID', 'The source quotation is unavailable in this business.');
  }
  const pos = input.placeOfSupplyOverride || customer.shippingStateCode || customer.stateCode || '';
  const params: IssueParams = {
    businessId: input.businessId,
    businessStateCode: business.stateCode,
    docType: 'SALES_ORDER',
    docDate: input.docDate,
    customerId: customer.id,
    partySnapshot: toPartySnapshot(customer),
    placeOfSupplyCode: pos,
    deliveryAddressLines: customer.shippingAddressLines ? [...customer.shippingAddressLines] : undefined,
    deliveryStateCode: customer.shippingStateCode,
    deliveryStateName: customer.shippingStateName,
    quotationId,
    lines: products.map(({ line, product, tax, sourceLine }) => ({
      productId: product.id,
      description: line.description?.trim() || product.name,
      hsnSac: sourceLine?.hsnSac ?? product.hsnSac,
      unit: line.unit ?? sourceLine?.unit ?? product.unit,
      quantityMilli: line.quantityMilli,
      ratePaise: line.ratePaise,
      taxRateBp: quotationId && line.sourceLineId ? sourceLine?.taxRateBp ?? 0 : tax?.rateBp ?? 0,
      cessRateBp: quotationId && line.sourceLineId ? sourceLine?.cessRateBp ?? 0 : product.cessRateBp,
      sourceLineId: line.sourceLineId,
      isStockTracked: product.isStockTracked,
    })), 
    notes: input.notes,
    terms: input.terms,
    roundOff: input.roundOff ?? false,
    status: 'DRAFT',
    ...(existing ? { replaceIdentity: { id: existing.id, docNumber: existing.docNumber } } : {}),
    user: { id: 'local', name: 'You' },
    now,
    deviceId,
    fyStartMonth: business.fyStartMonth,
  };

  const result = await db.transaction(
    'rw',
    [db.documents, db.documentLines, db.documentLinks, db.stockTransactions,
      db.documentSequences, db.auditLogs, db.parties, db.businesses, db.syncOutbox],
    async () => {
      if (quotationId) {
        const liveSource = await db.documents.get(quotationId);
        if (!liveSource || liveSource.docType !== 'QUOTATION' || liveSource.businessId !== input.businessId || liveSource.status !== 'ISSUED') {
          throw new SalesOrderServiceError('SOURCE_NOT_ISSUED', 'Issue the quotation before converting it to a sales order.');
        }
        const linkedLines = input.lines.filter((line) => line.sourceLineId);
        for (const line of linkedLines) {
          const sourceLine = await db.documentLines.get(line.sourceLineId as string);
          const orderLine = products.find((product) => product.line === line);
          if (!sourceLine || sourceLine.documentId !== quotationId || sourceLine.businessId !== input.businessId || sourceLine.productId !== orderLine?.product.id) {
            throw new SalesOrderServiceError('SOURCE_LINE_INVALID', 'A line no longer belongs to the source quotation or its original product.');
          }
        }
        const existingConversion = await db.documentLinks
          .where('toDocumentId').equals(quotationId)
          .and((link) => link.relation === 'CONVERTED_FROM' && link.fromDocType === 'SALES_ORDER' && link.fromDocumentId !== existing?.id)
          .first();
        if (existingConversion) throw new SalesOrderServiceError('ALREADY_CONVERTED', 'This quotation already has a sales order.');
      }
      return issueDocument(db as never, params);
    },
  );

  return { id: result.id, docNumber: result.docNumber, grandTotalPaise: result.grandTotalPaise };
}

export function createSalesOrderDraft(db: DNRDatabase, input: SalesOrderInput) {
  return buildSalesOrder(db, input);
}

export async function convertQuotationToSalesOrderDraft(
  db: DNRDatabase,
  input: { quotationId: string; docDate?: string; now?: string; deviceId?: string },
) {
  const quotation = await db.documents.get(input.quotationId);
  if (!quotation || quotation.docType !== 'QUOTATION') throw new SalesOrderServiceError('SOURCE_LINE_INVALID', 'Quotation not found.');
  if (quotation.status !== 'ISSUED') throw new SalesOrderServiceError('SOURCE_NOT_ISSUED', 'Issue the quotation before converting it to a sales order.');
  if (!quotation.customerId) throw new SalesOrderServiceError('PARTY_NOT_FOUND', 'The quotation has no customer.');
  const lines = await db.documentLines.where('documentId').equals(quotation.id).toArray();
  if (!lines.length) throw new SalesOrderServiceError('NO_LINES', 'The quotation has no lines to convert.');
  return buildSalesOrder(db, {
    businessId: quotation.businessId,
    customerId: quotation.customerId,
    docDate: input.docDate ?? new Date().toISOString().slice(0, 10),
    quotationId: quotation.id,
    lines: lines.map((line) => ({
      productId: line.productId ?? '',
      quantityMilli: line.quantityMilli,
      ratePaise: line.ratePaise,
      description: line.description,
      sourceLineId: line.id,
    })),
    roundOff: quotation.roundOffPaise !== 0,
    notes: quotation.notes,
    terms: quotation.terms,
    placeOfSupplyOverride: quotation.placeOfSupplyCode,
    now: input.now,
    deviceId: input.deviceId,
  });
}

export async function updateSalesOrderDraft(
  db: DNRDatabase,
  id: string,
  input: Omit<SalesOrderInput, 'businessId' | 'quotationId'>,
) {
  const existing = await db.documents.get(id);
  if (!existing || existing.docType !== 'SALES_ORDER') throw new SalesOrderServiceError('NOT_FOUND', 'Sales order not found.');
  if (existing.status !== 'DRAFT') throw new SalesOrderServiceError('NOT_EDITABLE', 'Issued sales orders are immutable.');
  return buildSalesOrder(db, {
    ...input,
    businessId: existing.businessId,
    quotationId: existing.quotationId,
    placeOfSupplyOverride: input.placeOfSupplyOverride ?? existing.placeOfSupplyCode,
  }, existing);
}

export async function deleteSalesOrderDraft(
  db: DNRDatabase,
  id: string,
  input: { now?: string; deviceId?: string } = {},
) {
  const now = input.now ?? new Date().toISOString();
  const deviceId = input.deviceId ?? await currentDeviceId(db);
  return db.transaction(
    'rw',
    [db.documents, db.documentLines, db.documentLinks, db.auditLogs, db.syncOutbox],
    async () => {
      const order = await db.documents.get(id);
      if (!order || order.docType !== 'SALES_ORDER') throw new SalesOrderServiceError('NOT_FOUND', 'Sales order not found.');
      if (order.status !== 'DRAFT') throw new SalesOrderServiceError('NOT_EDITABLE', 'Only a draft sales order can be deleted.');
      const children = await db.documentLinks.where('toDocumentId').equals(id).toArray();
      if (children.some((link) => link.fromDocumentId !== id)) {
        throw new SalesOrderServiceError('HAS_CHILDREN', 'This sales order is referenced by another document and cannot be deleted.');
      }

      const lines = await db.documentLines.where('documentId').equals(id).toArray();
      const links = await db.documentLinks.where('fromDocumentId').equals(id).toArray();
      await db.documentLines.where('documentId').equals(id).delete();
      await db.documentLinks.where('fromDocumentId').equals(id).delete();
      await db.documents.delete(id);
      await db.auditLogs.add({
        id: ulid(), businessId: order.businessId, entityType: 'DOCUMENT', entityId: id,
        action: 'DELETE', before: JSON.stringify({ docNumber: order.docNumber, status: order.status }),
        userId: 'local', userName: 'You', occurredAt: now,
      });
      await recordBusinessMutations(db as never, [
        { businessId: order.businessId, deviceId, entityType: 'document', entityId: id, operation: 'DELETE', version: nextVersion(order.syncVersion), changedAt: now, deletedAt: now },
        ...lines.map((line) => ({ businessId: line.businessId, deviceId, entityType: 'documentLine' as const, entityId: line.id, operation: 'DELETE' as const, version: nextVersion(line.syncVersion ?? 1), changedAt: now, deletedAt: now })),
        ...links.map((link) => ({ businessId: link.businessId, deviceId, entityType: 'documentLink' as const, entityId: link.id, operation: 'DELETE' as const, version: nextVersion(link.syncVersion ?? 1), changedAt: now, deletedAt: now })),
      ]);
      return { id };
    },
  );
}
